import {
  faCheckCircle,
  faChevronCircleRight,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { Dispatch, SetStateAction } from 'react';
import { TFunction } from 'react-i18next';

import { EmployeeUserDeviceCollection } from '../../../types/api/employees';
import { Unpacked } from '../../../types/util';
import { emptyCell, noColumnStyle } from '../../../utils/constants/tables';
import { dateFormat } from '../../../utils/helpers/formatting';
import { tryInt } from '../../../utils/helpers/parse';
import ActionsTableMenu from '../Components/ActionsTableMenu/ActionsTableMenu';

export type Row = Unpacked<EmployeeUserDeviceCollection>;

export const tableStorageKey = 'deviceManagement_dataTable';

export function getColumnHeadersMap(t: TFunction) {
  return {
    no: t('No.'),
    // id: t('ID Device'),
    activated: t('Status'),
    app_name: t('App name'),
    name: t('Name'),
    surname: t('Surname'),
    username: t('Username'),
    device_id: t('Device ID'),
    device_name: t('Device name'),
    created_at: t('Created'),
    last_login: t('Last login'),
    app_version: t('App version'),
  };
}

export function additionalColumnProperties(
  t: TFunction,
  column: keyof ReturnType<typeof getColumnHeadersMap> | 'actions',
  setContextMenuSelection: Dispatch<SetStateAction<any>>,
  setAction: (t: string) => void,
  setCaller: Dispatch<SetStateAction<'context-menu' | 'group-actions'>>,
  isApprove: boolean,
  isDeny: boolean,
  isDelete: boolean
) {
  switch (column) {
    case 'no':
      // case 'id':
      return {
        noColumnStyle,
        style: {
          width: 30,
          maxWidth: 30,
        },
        bodyStyle: {
          width: 30,
          maxWidth: 30,
          textAlign: 'center',
        },
      };

    case 'activated':
      return {
        body: (row: Row) => {
          const isActive = !!parseInt(row[column]);

          return (
            <span
              data-tip={isActive ? t('Active') : t('Inactive')}
              data-for="tooltip-right"
            >
              <FontAwesomeIcon icon={isActive ? faCheckCircle : faTimes} />
            </span>
          );
        },
        style: {
          width: 60,
          maxWidth: 110,
        },
        bodyStyle: {
          width: 60,
          maxWidth: 110,
          display: 'flex',
          justifyContent: 'center',
        },
      };

    case 'device_id':
      return {
        body: (row: Row) => (
          <span data-tip={row[column]} data-for="tooltip-right">
            {`${row[column]?.slice(0, 8)} ... `}
            <FontAwesomeIcon
              icon={faChevronCircleRight}
              className="secondary-icon"
            />
          </span>
        ),
        style: {
          width: 160,
        },
        bodyStyle: {
          width: 160,
        },
      };

    case 'created_at':
    case 'last_login':
      return {
        body: (row: any) =>
          row[column] ? (
            <span data-tip={dateFormat(row[column]!)} data-for="tooltip-right">
              {dayjs(row[column]!).fromNow()}
            </span>
          ) : (
            emptyCell
          ),
        style: {
          width: 180,
        },
        bodyStyle: {
          width: 180,
        },
      };

    case 'name':
    case 'surname':
      return {
        style: {
          width: 180,
        },
        bodyStyle: {
          width: 180,
        },
      };

    case 'username':
    case 'device_name':
      return {
        style: {
          width: 180,
        },
        bodyStyle: {
          width: 180,
        },
      };

    case 'actions':
      return {
        body: (row: any) => {
          const isActivated = !!tryInt(row.activated);

          return (
            <ActionsTableMenu
              actions={[
                {
                  label: t('Approve'),
                  icon: 'fas fa-check-circle',
                  disabled: isActivated && isApprove,
                  command: () => {
                    setContextMenuSelection(row);
                    setAction('approve');
                    setCaller('context-menu');
                  },
                },
                {
                  label: t('Deny'),
                  icon: 'fas fa-ban',
                  disabled: isActivated && isDeny,
                  command: () => {
                    setContextMenuSelection(row);
                    setAction('deny');
                    setCaller('context-menu');
                  },
                },
                {
                  label: t('Delete'),
                  icon: 'fas fa-trash',
                  disabled: !isActivated && isDelete,
                  command: () => {
                    setContextMenuSelection(row);
                    setCaller('context-menu');
                    setAction('delete');
                  },
                },
              ]}
            />
          );
        },
        style: {
          width: 60,
          borderLeft: '1px solid #e9ecef',
          flex: '0 1 auto',
          position: 'sticky',
        },
        bodyStyle: {
          width: 60,
          borderLeft: '1px solid #e9ecef',
          flex: '0 1 auto',
          position: 'sticky',
          justifyContent: 'center',
        },
      };

    default:
      return {
        style: {
          width: 200,
        },
        bodyStyle: {
          width: 200,
        },
      };
  }
}
